import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft, Facebook } from "lucide-react";

const ABOUT_BG =
  "https://images.unsplash.com/photo-1688981935353-07e2d0142648?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxsaXZlJTIwbWV0YWwlMjBjb25jZXJ0JTIwY3Jvd2QlMjBkYXJrfGVufDB8fHx8MTc3ODQ2MTY3M3ww&ixlib=rb-4.1.0&q=85";

export default function AboutPage() {
  return (
    <div className="relative min-h-screen bg-zinc-950 text-zinc-100" data-testid="about-page">
      <Header />

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${ABOUT_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(100%) contrast(1.4)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(9,9,11,0.6) 0%, rgba(9,9,11,0.92) 70%, rgba(9,9,11,1) 100%)",
          }}
        />

        <div className="relative px-4 md:px-10 py-12 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-zinc-300 hover:text-red-500 mb-8 transition-colors"
            data-testid="about-back-home"
          >
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>

          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-4">
            //_THE_STORY · EST. 2007 · CONNECTICUT, USA
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tighter text-zinc-50 glitch select-none">
            EST. <span className="text-red-600">2007</span>.
          </h1>
          <div className="mt-3 font-display text-2xl md:text-4xl tracking-tight text-zinc-100 max-w-3xl">
            A REVOLUTION BUILT FROM <span className="text-red-600">PASSION</span>.
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative px-4 md:px-10 py-16 md:py-24">
        <article className="max-w-3xl mx-auto space-y-8 text-base md:text-lg leading-relaxed text-zinc-100">
          <p>
            I've been hooked on hard rock and heavy metal my entire life. It
            started with <span className="text-red-500 font-display tracking-tight">Black Sabbath</span> —
            that doom-soaked riff, Ozzy's voice, the way the whole room felt
            heavier when the needle dropped. From there it never stopped.
            Every band that walked through Sabbath's doorway became part of my
            soundtrack — the loud, the dirty, the sacred.
          </p>

          <p>
            Then the internet hit. Independent stations started popping up
            everywhere, run by regular people on regular machines, putting out
            real music. I watched it happen and one thought kept hammering me:
          </p>

          <p className="font-display text-3xl md:text-5xl tracking-tight text-zinc-50 border-l-4 border-red-600 pl-6 my-12 leading-tight">
            "WHY NOT ME?"
          </p>

          <p>
            In <span className="text-red-500 font-display">2007</span>, from
            my office here in Connecticut, I broadcast the first-ever show on{" "}
            <strong className="text-zinc-50">Digital Revolution Radio</strong>.
            I was terrified. The signal was going out to the entire planet
            and I was sitting alone with a mic and a stack of records. But
            once the first track hit, I knew this was where I was supposed to
            be.
          </p>

          <p>
            I wasn't on this island for long. The first two DJs to ride with
            me were{" "}
            <span className="text-red-500 font-display">DJ Kiddman</span> and{" "}
            <span className="text-red-500 font-display">DJ Titan</span> — two
            humans who lived and breathed the same music I did. They didn't
            just sign on; they helped build the foundation. From there the
            crew grew. Real DJs. Real fans. People who would die before
            reading an ad about car insurance.
          </p>

          <p>
            Along the way I made friends with bands, labels, promoters, and
            countless listeners who became family. We celebrated wins
            together. We mourned losses together. We've spun records for
            people in apartments in Tokyo, kitchens in Berlin, garages in
            Texas, and basements right here in New England.
          </p>

          <div className="border-t border-b border-zinc-800 py-10 my-12">
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_WHAT_IT_MEANS_TO_ME
            </div>
            <p className="font-display text-2xl md:text-4xl tracking-tight text-zinc-50 leading-tight">
              Digital Revolution Radio keeps me{" "}
              <span className="text-red-600">LIFTED</span>. It brings me joy
              knowing that every single day, people from all over the world
              tune in to hear the <span className="text-red-600">BEST ROCK AND METAL</span> we've
              got.
            </p>
          </div>

          <p>
            Almost two decades later, we're still here. Still loud. Still
            independent. Still picking every track by hand and refusing to
            let this music get drowned out by the algorithm.
          </p>

          <p className="font-display text-2xl md:text-3xl tracking-tight text-zinc-50">
            You hear it here,{" "}
            <span className="text-red-600">"LOUD AND CLEAR."</span>
          </p>

          {/* Signature card with founder photo */}
          <div className="mt-16 border-2 border-red-600 bg-black p-6 md:p-8 flex flex-col sm:flex-row items-start gap-6">
            <div className="relative w-full sm:w-44 md:w-52 shrink-0 aspect-square overflow-hidden border-2 border-zinc-800 self-center sm:self-start">
              <img
                src="https://customer-assets.emergentagent.com/job_thunder-stream-1/artifacts/u5ob8l0v_Lady%20Sondra.jpg"
                alt="Sondra Sego — Founder of Digital Revolution Radio"
                className="w-full h-full object-cover"
                data-testid="founder-photo"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 60%, rgba(220,38,38,0.15) 100%)",
                }}
              />
            </div>
            <div className="flex-1">
              <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
                //_FOUNDER
              </div>
              <div className="font-display text-3xl md:text-4xl text-zinc-50 leading-none mb-2 tracking-tight">
                SONDRA <span className="text-red-600">SEGO</span>
              </div>
              <div className="text-xs text-zinc-300 tracking-widest uppercase">
                Founder · Owner · Lifelong Headbanger · Connecticut, USA
              </div>
              <div className="mt-4 inline-block bg-red-600 text-black font-display text-[10px] tracking-widest uppercase px-2 py-1">
                AKA · LADY SONDRA
              </div>
              <div className="mt-5">
                <a
                  href="https://www.facebook.com/sondralupuliosego/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-600 hover:bg-red-600 hover:text-black text-zinc-100 font-display text-xs tracking-[0.25em] uppercase px-3 py-2 transition-colors"
                  data-testid="founder-facebook-link"
                >
                  <Facebook className="w-3.5 h-3.5" />
                  Follow on Facebook
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-white hover:text-black text-white font-display tracking-widest uppercase px-6 py-3 transition-colors border border-zinc-700"
              data-testid="about-tune-in-btn"
            >
              ▶ Tune In
            </Link>
          </div>
        </article>
      </section>

      <Footer />
    </div>
  );
}
